import { Button, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material'
import { ArrowDropDownIcon } from '@mui/x-date-pickers'
import { MouseEvent, useRef, useState } from 'react'

export interface SplitButtonProps {
  options: string[]
  onFocusChild?: (index: number) => void
  disabledOptions?: number[] // Array of indices for disabled options
  disabled?: boolean // Whether the entire button group is disabled
  isRawDataEnabled?: boolean
  defaultIndex?: number // Explicit default selected index; takes priority over isRawDataEnabled
}

export default function SplitButton({
  options,
  onFocusChild,
  disabledOptions = [],
  disabled = false,
  isRawDataEnabled,
  defaultIndex,
}: SplitButtonProps) {
  const [open, setOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(
    defaultIndex !== undefined ? defaultIndex : isRawDataEnabled ? 2 : 0,
  )
  const anchorRef = useRef<HTMLDivElement>(null)

  const handleMenuItemClick = (event: MouseEvent<HTMLLIElement>, index: number) => {
    console.log(event)
    setSelectedIndex(index)
    setOpen(false)
    if (onFocusChild) onFocusChild(index)
  }

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const handleClose = (event: Event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return
    }
    setOpen(false)
  }

  return (
    <ButtonGroup
      variant="contained"
      ref={anchorRef}
      disabled={disabled}
      sx={{
        color: 'text.primary',
      }}
    >
      <Button data-testid="split-button-name" onClick={() => onFocusChild && onFocusChild(selectedIndex)}>
        {options[selectedIndex]}
      </Button>
      <Button
        size="small"
        aria-controls={open ? 'split-button-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="menu"
        data-testid="split-button"
        onClick={handleToggle}
      >
        <ArrowDropDownIcon />
      </Button>
      <Popper sx={{ zIndex: 1 }} open={open} anchorEl={anchorRef.current} role={undefined} transition disablePortal>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  {options.map((option, index) => (
                    <MenuItem
                      key={option}
                      data-testid={option}
                      disabled={disabledOptions.includes(index)}
                      selected={index === selectedIndex}
                      onClick={(event) => handleMenuItemClick(event, index)}
                    >
                      {option}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </ButtonGroup>
  )
}
